import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  MessageSquare,
  Calendar,
  FileText,
  Zap,
  Bot,
  Clock,
  Sparkles,
  Lock,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useLocationStore } from "@/lib/store";
import { tierUnlocks, type PlanTier, type TierFeature, type Location } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

interface RecentAction {
  id: string;
  type: "post_published" | "post_generated" | "review_auto_replied" | "weekly_report_generated";
  action: string;
  timestamp: string;
}

interface AutomationRow {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  tokenCost: string;
  field: keyof Location;
  feature: TierFeature;
}

const automationRows: AutomationRow[] = [
  {
    key: "reply",
    label: "Auto-Reply to Reviews",
    description: "AI drafts and sends review responses within 2 hours of posting",
    icon: <MessageSquare className="w-5 h-5" />,
    tokenCost: "2 tokens / reply",
    field: "autoReplyReviews",
    feature: "autoReplyReviews",
  },
  {
    key: "post",
    label: "Auto-Schedule GBP Posts",
    description: "AI generates and schedules weekly Google Business Profile posts",
    icon: <Calendar className="w-5 h-5" />,
    tokenCost: "3 tokens / post",
    field: "autoPostSchedule",
    feature: "autoSchedulePosts",
  },
  {
    key: "reports",
    label: "Weekly Reports",
    description: "AI generates and emails a performance summary every Monday",
    icon: <FileText className="w-5 h-5" />,
    tokenCost: "Free",
    field: "autoWeeklyReports",
    feature: "weeklyReports",
  },
];

export default function AutomationPage() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();
  const { user } = useAuth();
  const tier: PlanTier = ((user?.tier as PlanTier) || "free");

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;
  const location = locations?.find(l => l.id === locationId);

  const { data: recentActions, isLoading: actionsLoading } = useQuery<RecentAction[]>({
    queryKey: ["/api/locations", locationId, "automation/recent-actions"],
    enabled: !!locationId,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ field, value }: { field: string; value: boolean }) => {
      await apiRequest("PATCH", `/api/locations/${locationId}/automation`, { [field]: value });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations"] });
      toast({ title: "Automation setting updated" });
    },
  });

  const enabledCount = location
    ? automationRows.filter(r => location[r.field] as boolean).length
    : 0;

  if (!location) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="text-automation-title">
            <Zap className="w-6 h-6 text-brand-orange" />
            Automation
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Let AI handle the repetitive tasks while you focus on your business
          </p>
        </div>
        <Badge variant="secondary" className="text-sm px-3 py-1" data-testid="text-active-count">
          <Bot className="w-4 h-4 mr-1" />
          {enabledCount} / {automationRows.length} Active
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          {automationRows.map((row) => {
            const isEnabled = location[row.field] as boolean;
            const unlocked = tierUnlocks(tier, row.feature);
            return (
              <Card
                key={row.key}
                className={`border transition-colors ${
                  !unlocked
                    ? "border-border/40 bg-muted/20"
                    : isEnabled
                      ? "border-brand-orange/30 bg-brand-orange/5"
                      : "border-border/50"
                }`}
                data-testid={`card-automation-${row.key}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      !unlocked
                        ? "bg-muted text-muted-foreground"
                        : isEnabled
                          ? "bg-brand-orange/20 text-brand-orange"
                          : "bg-muted text-muted-foreground"
                    }`}>
                      {row.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-sm font-semibold">{row.label}</h3>
                        <Badge variant="outline" className="text-xs">
                          {row.tokenCost}
                        </Badge>
                        {!unlocked && (
                          <Badge
                            variant="secondary"
                            className="text-xs gap-1"
                            data-testid={`badge-locked-${row.key}`}
                          >
                            <Lock className="w-3 h-3" />
                            Growth+
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{row.description}</p>
                    </div>
                    {unlocked ? (
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) =>
                          toggleMutation.mutate({ field: row.field, value: checked })
                        }
                        disabled={toggleMutation.isPending}
                        data-testid={`switch-${row.key}`}
                      />
                    ) : (
                      <Link href="/pricing">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 shrink-0"
                          data-testid={`button-upgrade-${row.key}`}
                        >
                          <Lock className="w-3 h-3" />
                          Upgrade
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-brand-orange" />
                Recent AI Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {actionsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : !recentActions || recentActions.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2" data-testid="text-no-recent-actions">
                  No automation activity yet. Once your enabled automations run, their actions will appear here.
                </p>
              ) : (
                recentActions.map((action) => (
                  <div
                    key={action.id}
                    className="flex items-start gap-3 pb-3 border-b border-border/30 last:border-0 last:pb-0"
                    data-testid={`row-recent-action-${action.id}`}
                  >
                    <div className="w-2 h-2 rounded-full bg-brand-orange mt-1.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs leading-relaxed break-words" data-testid={`text-action-${action.id}`}>
                        {action.action}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDistanceToNow(new Date(action.timestamp), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="bg-brand-orange/5 border-brand-orange/20">
            <CardContent className="p-5 text-center">
              <Bot className="w-10 h-10 mx-auto text-brand-orange mb-3" />
              <h3 className="text-sm font-semibold mb-1">AI Working For You</h3>
              <p className="text-xs text-muted-foreground">
                Automations run 24/7 in the background. Token costs are deducted automatically from your balance.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
